import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import {
  getAccelerator,
  getConfig,
  getModels,
  getProfiles,
  getServerXE7740,
  getThroughput,
} from '../lib/repo';
import type { CatalogData } from '../types';
import { materializeCatalogFromQueryData } from './catalogUtils';
import { useSizingRunStatus } from './useSizingRunStatus';
import { getCatalogThroughputVersion, hashCatalogSnapshot } from '@/lib/catalogSnapshot';
import { trace, createTrace } from '@/lib/logging';
import { buildFallbackCatalog } from '@/lib/fallbackCatalog';
import { normalizeModelId } from '@/lib/catalog/keys';

export type { CatalogData };

const CATALOG_DEBOUNCE_MS = 300;

export function useCatalog() {
  const debugCatalog = (...args: unknown[]) => console.debug('[CatalogHook]', ...args);
  
  // Phase 2.15: Query options to prevent refetch storms
  const queryOptions = useMemo(() => ({
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }), []);
  
  const acceleratorQuery = useQuery({
    queryKey: ['catalog', 'accelerator'],
    queryFn: getAccelerator,
    ...queryOptions,
  });

  const serverQuery = useQuery({
    queryKey: ['catalog', 'server'],
    queryFn: getServerXE7740,
    ...queryOptions,
  });

  const modelsQuery = useQuery({
    queryKey: ['catalog', 'models'],
    queryFn: getModels,
    ...queryOptions,
  });

  const throughputQuery = useQuery({
    queryKey: ['catalog', 'throughput'],
    queryFn: getThroughput,
    ...queryOptions,
  });

  const profilesQuery = useQuery({
    queryKey: ['catalog', 'profiles'],
    queryFn: getProfiles,
    ...queryOptions,
  });

  const configQuery = useQuery({
    queryKey: ['catalog', 'config'],
    queryFn: getConfig,
    ...queryOptions,
  });

  // Phase 2.15: Primitive-only logging guard to prevent spam on every render
  const lastLogRef = useRef('');
  
  useEffect(() => {
    const key = `${acceleratorQuery.status}|${acceleratorQuery.isFetching}|${Boolean(acceleratorQuery.data)}|${Boolean(acceleratorQuery.error)}|${modelsQuery.status}|${modelsQuery.isFetching}|${modelsQuery.data?.length ?? 0}|${throughputQuery.status}|${throughputQuery.isFetching}|${throughputQuery.data?.length ?? 0}`;
    
    if (lastLogRef.current !== key) {
      debugCatalog('Supabase catalog queries updated', {
        accelerator: {
          status: acceleratorQuery.status,
          isFetching: acceleratorQuery.isFetching,
          hasData: Boolean(acceleratorQuery.data),
          error: acceleratorQuery.error ? String(acceleratorQuery.error) : null,
        },
        server: {
          status: serverQuery.status,
          isFetching: serverQuery.isFetching,
          hasData: Boolean(serverQuery.data),
          error: serverQuery.error ? String(serverQuery.error) : null,
        },
        models: {
          status: modelsQuery.status,
          isFetching: modelsQuery.isFetching,
          count: modelsQuery.data?.length,
          error: modelsQuery.error ? String(modelsQuery.error) : null,
        },
        throughput: {
          status: throughputQuery.status,
          isFetching: throughputQuery.isFetching,
          count: throughputQuery.data?.length,
          error: throughputQuery.error ? String(throughputQuery.error) : null,
        },
        profiles: {
          status: profilesQuery.status,
          isFetching: profilesQuery.isFetching,
          count: profilesQuery.data?.length,
          error: profilesQuery.error ? String(profilesQuery.error) : null,
        },
        config: {
          status: configQuery.status,
          isFetching: configQuery.isFetching,
          hasData: Boolean(configQuery.data),
          error: configQuery.error ? String(configQuery.error) : null,
        },
      });
      lastLogRef.current = key;
    }
  }, [acceleratorQuery.status, acceleratorQuery.isFetching, acceleratorQuery.data, acceleratorQuery.error, serverQuery.status, serverQuery.isFetching, serverQuery.data, serverQuery.error, modelsQuery.status, modelsQuery.isFetching, modelsQuery.data, modelsQuery.error, throughputQuery.status, throughputQuery.isFetching, throughputQuery.data, throughputQuery.error, profilesQuery.status, profilesQuery.isFetching, profilesQuery.data, profilesQuery.error, configQuery.status, configQuery.isFetching, configQuery.data, configQuery.error]);

  const materialized = useMemo(() => {
    // Wait for critical queries to finish loading before materializing
    if (modelsQuery.isLoading || throughputQuery.isLoading) {
      debugCatalog('Waiting for queries to complete...', {
        modelsLoading: modelsQuery.isLoading,
        throughputLoading: throughputQuery.isLoading,
      });
      // Return empty catalog structure to prevent premature materialization
      return {
        catalog: null,
        usedFallbackPair: false,
      };
    }
    
    return materializeCatalogFromQueryData({
      accelerator: acceleratorQuery.data,
      server: serverQuery.data,
      models: modelsQuery.data,
      throughput: throughputQuery.data,
      profiles: profilesQuery.data,
      config: configQuery.data,
    });
  }, [
    modelsQuery.isLoading,
    throughputQuery.isLoading,
    acceleratorQuery.data,
    serverQuery.data,
    modelsQuery.data,
    throughputQuery.data,
    profilesQuery.data,
    configQuery.data,
  ]);

  const [catalogState, setCatalogState] = useState<CatalogData>(buildFallbackCatalog());
  const [usedFallbackState, setUsedFallbackState] = useState(materialized.usedFallbackPair);
  const catalogHashRef = useRef(hashCatalogSnapshot(materialized.catalog));
  const catalogVersionRef = useRef(getCatalogThroughputVersion(materialized.catalog));
  const fallbackRef = useRef(materialized.usedFallbackPair);
  const hydrationFlagRef = useRef(false);
  const hydrationNoticeRef = useRef<'hydrated' | 'withheld' | 'unknown'>('unknown');
  const materializedModelsCount = materialized.catalog?.models?.length ?? 0;
  const materializedThroughputCount = materialized.catalog?.throughput?.length ?? 0;
  const [catalogCommitted, setCatalogCommitted] = useState(false);
  const [catalogHashCommitted, setCatalogHashCommitted] = useState<string | null>(catalogHashRef.current);
  const [committedThroughputCount, setCommittedThroughputCount] = useState(materializedThroughputCount);
  const [committedModelsCount, setCommittedModelsCount] = useState(materializedModelsCount);
  const pendingSnapshotRef = useRef<
    | {
        catalog: CatalogData | null;
        usedFallbackPair: boolean;
        hash: string;
        throughputVersion: number;
      }
    | null
  >(null);
  const allQueriesLoadedRef = useRef(false);
  const { isFrozen: isCatalogFrozen } = useSizingRunStatus();

  // Phase 1: Use ref-based approach to stabilize debounce
  const commitRef = useRef<((payload: {
    catalog: CatalogData | null;
    usedFallbackPair: boolean;
    hash: string;
    throughputVersion: number;
    deferred?: boolean;
  }) => void) | null>(null);

  commitRef.current = (payload) => {
    catalogHashRef.current = payload.hash;
    catalogVersionRef.current = payload.throughputVersion;
    fallbackRef.current = payload.usedFallbackPair;
    setCatalogState(payload.catalog);
    setUsedFallbackState(payload.usedFallbackPair);
    const throughputCount = payload.catalog?.throughput?.length ?? 0;
    const modelsCount = payload.catalog?.models?.length ?? 0;
    
    trace(createTrace('catalog', 'useCatalog', 'catalog-ready', 'Catalog committed', {
      hashPrefix: payload.hash.slice(0, 12),
      throughputVersion: payload.throughputVersion,
      usedFallback: payload.usedFallbackPair,
      throughputCount,
      modelsCount,
      deferred: Boolean(payload.deferred),
    }));
    
    console.log('[📦 CATALOG COMMIT]', {
      hash: payload.hash.slice(0, 24),
      throughputVersion: payload.throughputVersion,
      usedFallbackPair: payload.usedFallbackPair,
      throughputCount,
      modelsCount,
      deferredDuringRun: Boolean(payload.deferred),
    });

    const sampleThroughput = payload.catalog?.throughput?.[0];
    if (sampleThroughput) {
      console.debug('[CatalogHook] Throughput sample on commit', {
        usedFallbackPair: payload.usedFallbackPair,
        modelId: sampleThroughput.model_id,
        precision: sampleThroughput.precision,
        batch: sampleThroughput.batch,
        backend: sampleThroughput.backend,
        prompt_tps: sampleThroughput.prompt_tps,
        decode_tps: sampleThroughput.decode_tps,
      });
    } else {
      console.warn('[CatalogHook] No throughput rows at commit time', {
        usedFallbackPair: payload.usedFallbackPair,
        throughputCount,
      });
    }

    setCatalogCommitted(true);
    setCatalogHashCommitted(payload.hash);
    setCommittedThroughputCount(throughputCount);
    setCommittedModelsCount(modelsCount);
  };

  const debouncedCommit = useMemo(
    () => debounce((payload: typeof commitRef.current extends (p: infer P) => void ? P : never) => {
      commitRef.current?.(payload);
    }, CATALOG_DEBOUNCE_MS),
    [], // Empty deps - debounce function is stable
  );

  // Track when all queries have loaded at least once
  const allQueriesLoaded = 
    !acceleratorQuery.isLoading &&
    !serverQuery.isLoading &&
    !modelsQuery.isLoading &&
    !throughputQuery.isLoading &&
    !profilesQuery.isLoading &&
    !configQuery.isLoading;

  useEffect(() => {
    const hasModelsData = materializedModelsCount > 0;
    const hasThroughputData = materializedThroughputCount > 0;
    const hydrationEligible =
      allQueriesLoaded && hasModelsData && hasThroughputData && !materialized.usedFallbackPair;

    if (allQueriesLoaded && !allQueriesLoadedRef.current) {
      allQueriesLoadedRef.current = true;

      trace(
        createTrace('catalog', 'useCatalog', 'catalog-ready', 'All queries loaded', {
          acceleratorOk: Boolean(acceleratorQuery.data),
          serverOk: Boolean(serverQuery.data),
          modelsCount: modelsQuery.data?.length ?? 0,
          throughputCount: throughputQuery.data?.length ?? 0,
          profilesCount: profilesQuery.data?.length ?? 0,
          configOk: Boolean(configQuery.data),
          hydrationEligible,
          usedFallbackPair: materialized.usedFallbackPair,
          hasModelsData,
          hasThroughputData,
        })
      );

      debugCatalog('All catalog queries loaded for first time', {
        hydrationEligible,
        usedFallbackPair: materialized.usedFallbackPair,
        hasModelsData,
        hasThroughputData,
      });
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (hydrationEligible) {
      if (!hydrationFlagRef.current) {
        (window as any).__catalogHydrated__ = true;
        hydrationFlagRef.current = true;
        hydrationNoticeRef.current = 'hydrated';
        console.log('[CatalogHook] ✅ Catalog hydration ready (Supabase data)');
        debugCatalog('✅ Catalog hydration ready (Supabase data)', {
          modelsCount: materializedModelsCount,
          throughputCount: materializedThroughputCount,
        });
      }
      return;
    }

    delete (window as any).__catalogHydrated__;

    if (hydrationFlagRef.current) {
      hydrationFlagRef.current = false;
      hydrationNoticeRef.current = 'withheld';
      console.log('[CatalogHook] ♻️ Catalog hydration reset (non-Supabase data)', {
        usedFallbackPair: materialized.usedFallbackPair,
        hasModelsData,
        hasThroughputData,
        allQueriesLoaded,
      });
      debugCatalog('♻️ Catalog hydration reset (non-Supabase data)', {
        usedFallbackPair: materialized.usedFallbackPair,
        hasModelsData,
        hasThroughputData,
        allQueriesLoaded,
      });
      return;
    }

    if (hydrationNoticeRef.current !== 'withheld') {
      hydrationNoticeRef.current = 'withheld';
      console.log('[CatalogHook] ⏳ Catalog hydration withheld until Supabase data resolves', {
        usedFallbackPair: materialized.usedFallbackPair,
        hasModelsData,
        hasThroughputData,
        allQueriesLoaded,
      });
      debugCatalog('⏳ Catalog hydration withheld until Supabase data resolves', {
        usedFallbackPair: materialized.usedFallbackPair,
        hasModelsData,
        hasThroughputData,
        allQueriesLoaded,
      });
    }
  }, [
    allQueriesLoaded,
    acceleratorQuery.data,
    serverQuery.data,
    modelsQuery.data,
    throughputQuery.data,
    profilesQuery.data,
    configQuery.data,
    materializedModelsCount,
    materializedThroughputCount,
    materialized.usedFallbackPair,
  ]);

  useEffect(() => {
    // Only commit catalog changes after all queries have loaded at least once
    if (!allQueriesLoadedRef.current) {
      debouncedCommit.cancel();
      pendingSnapshotRef.current = null;
      return undefined;
    }

    // Skip commit if catalog is still loading (null)
    if (materialized.catalog === null) {
      debugCatalog('Skipping commit - catalog still materializing');
      return undefined;
    }

    const nextHash = hashCatalogSnapshot(materialized.catalog);
    const nextFallback = materialized.usedFallbackPair;
    const nextVersion = getCatalogThroughputVersion(materialized.catalog);
    const shouldUpdate = nextHash !== catalogHashRef.current || nextFallback !== fallbackRef.current;

    if (!shouldUpdate) {
      if (!isCatalogFrozen) {
        pendingSnapshotRef.current = null;
      }
      return undefined;
    }

    const payload = {
      catalog: materialized.catalog,
      usedFallbackPair: nextFallback,
      hash: nextHash,
      throughputVersion: nextVersion,
    };

    const activeRunInProgress = typeof window !== 'undefined' && (window as any).__activeRunInProgress__;
    
    if (isCatalogFrozen || activeRunInProgress) {
      pendingSnapshotRef.current = payload;
      debouncedCommit.cancel();
      debugCatalog('Catalog update deferred', {
        hash: nextHash.slice(0, 24),
        throughputVersion: nextVersion,
        usedFallbackPair: nextFallback,
        frozen: isCatalogFrozen,
        runInProgress: activeRunInProgress,
      });
      return undefined;
    }

    const wasDeferred = pendingSnapshotRef.current?.hash === nextHash;
    pendingSnapshotRef.current = null;
    debouncedCommit({ ...payload, deferred: wasDeferred });

    return () => {
      debouncedCommit.cancel();
    };
  }, [materialized, isCatalogFrozen, debouncedCommit]);

  useEffect(() => {
    if (!isCatalogFrozen && pendingSnapshotRef.current) {
      const snapshot = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      if (snapshot.throughputVersion > catalogVersionRef.current) {
        debouncedCommit({ ...snapshot, deferred: true });
      } else {
        debugCatalog('Discarded deferred catalog update without version bump', {
          hash: snapshot.hash.slice(0, 24),
          pendingVersion: snapshot.throughputVersion,
          currentVersion: catalogVersionRef.current,
        });
      }
    }
  }, [isCatalogFrozen, debouncedCommit]);

  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const isLoading =
    acceleratorQuery.isLoading ||
    serverQuery.isLoading ||
    modelsQuery.isLoading ||
    throughputQuery.isLoading ||
    profilesQuery.isLoading ||
    configQuery.isLoading;

  const error =
    acceleratorQuery.error ||
    serverQuery.error ||
    modelsQuery.error ||
    throughputQuery.error ||
    profilesQuery.error ||
    configQuery.error ||
    null;

  // Phase 2.15: Primitive-only logging guard for materialization
  const lastMaterializeLogRef = useRef('');
  
  useEffect(() => {
    const key = `${catalogState.models?.length ?? 0}|${catalogState.throughput?.length ?? 0}|${catalogState.profiles?.length ?? 0}|${usedFallbackState}`;
    
    if (lastMaterializeLogRef.current !== key) {
      const fallbackUsage = {
        acceleratorFallback: !acceleratorQuery.data,
        serverFallback: !serverQuery.data,
        modelsFallback: usedFallbackState || !modelsQuery.data,
        throughputFallback: usedFallbackState || !throughputQuery.data,
        profilesFallback: !profilesQuery.data,
        configFallback: !configQuery.data,
        modelsThroughputPairFallback: usedFallbackState,
      };
      debugCatalog('Catalog materialized for sizing workflow', {
        fallbackUsage,
        acceleratorModel: catalogState.accelerator?.display_name,
        serverSku: catalogState.server?.server_sku,
        throughputPoints: catalogState.throughput?.length,
        profileCount: catalogState.profiles?.length,
      });
      lastMaterializeLogRef.current = key;
    }
  }, [catalogState.models?.length, catalogState.throughput?.length, catalogState.profiles?.length, usedFallbackState, acceleratorQuery.data, serverQuery.data, modelsQuery.data, throughputQuery.data, profilesQuery.data, configQuery.data, catalogState.accelerator?.display_name, catalogState.server?.server_sku]);

  // Phase 2.11: Catalog Readiness + Fixtures Fallback
  const catalogReady = Boolean(
    !modelsQuery.isLoading &&
    !throughputQuery.isLoading &&
    catalogState.models &&
    catalogState.models.length > 0 &&
    catalogState.throughput &&
    catalogState.throughput.length > 0 &&
    !usedFallbackState
  );
  
  const provenance: 'supabase' | 'fallback' = usedFallbackState ? 'fallback' : 'supabase';

  const catalog = useMemo(() => {
    return { ...catalogState, ready: catalogReady, provenance };
  }, [
    catalogState.accelerator,
    catalogState.server,
    catalogState.models,
    catalogState.throughput,
    catalogState.profiles,
    catalogState.config,
    catalogReady,
    provenance,
  ]);

  return {
    acceleratorQuery,
    serverQuery,
    modelsQuery,
    throughputQuery,
    profilesQuery,
    configQuery,
    catalog,
    catalogCommitted,
    catalogHash: catalogHashCommitted,
    committedThroughputCount,
    committedModelsCount,
    isLoading,
    error,
  };
}

export function catalogContainsModelId(
  catalog: CatalogData | null | undefined,
  modelId: string | null | undefined,
): boolean {
  if (!catalog || !catalog.models || !modelId) {
    return false;
  }
  const normalizedTarget = normalizeModelId(modelId);
  if (!normalizedTarget) {
    return false;
  }
  return catalog.models.some((model) => normalizeModelId(model.model_id) === normalizedTarget);
}

export function getDefaultCatalogModelId(catalog: CatalogData | null | undefined): string | null {
  if (!catalog || !catalog.models || catalog.models.length === 0) {
    return null;
  }
  return catalog.models[0]?.model_id ?? null;
}
export { getCatalogKey } from '@/lib/catalogSnapshot';
export { getCatalogThroughputVersion } from '@/lib/catalogSnapshot';
